import React from 'react'
import {
  alpha, Box, Button, Container, Typography, useTheme 
} from '@mui/material'
import { useMeasure } from '@react-hookz/web/esnext'
import { Link as GatsbyLink } from 'gatsby'

import { rudderslabTrackOnClick } from '~/utils/common'
import { PricingCard, PricingCardType, MobilePricingTabs } from '@components/templates/pricing/PricingCard'
import {Callout} from '@components/Callout'

type HeaderProps = {
  herobannertitle: string
  description: string
  buttonText: string
  buttonLink: string
  pricing_cards: PricingCardType[]
  callout?: {
    useCallout: boolean
    icon?: string
    title: string
    description?: string
    buttonText: string
    buttonContained?: boolean
    link: string
  }
}

export const Header = ({
  herobannertitle,
  description,
  buttonText,
  buttonLink,
  pricing_cards: pricingCards,
  callout
}: HeaderProps) => {
  const { palette } = useTheme()
  const [measurements, cardsRef] = useMeasure()
  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        zIndex: 1,
        position: 'relative',
        pt: {
          xs: 4,
          laptop: 10,
        },
        '&:before': {
          pointerEvents: 'none',
          content: "' '",
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: `calc(100% - ${(measurements?.bottom ?? 0) + 200}px)`,
          background: `linear-gradient(
            -3deg,
            ${alpha(palette.success.main, 0)} 0%,
            ${alpha(palette.success.main, 0)} 40%,
            ${alpha(palette.success.main, 0.15)} 40%,
            ${alpha(palette.success.main, 0.1)} 50%,
            ${alpha('rgb(255, 165, 0)', 0.1)} 100%
          )`,
        }
      }}>
      <Box
        sx={{
          px: 3, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 12,
          mb: {
            xs: 6,
            tablet: 8
          },
          mx: 'auto',
          maxWidth: 700
        }}>
        <Typography variant="h1" fontWeight={500} sx={{ mb: 4, textAlign: 'center' }}>{herobannertitle}</Typography>
        <Typography sx={{ mb: 4, textAlign: 'center' }}>{description}</Typography>
        <Button
          variant="contained"
          component={buttonLink ? 'a' : GatsbyLink}
          {...{
            [buttonLink ? 'href' : 'to']: buttonLink
          }}
          onClick={e => rudderslabTrackOnClick('button', herobannertitle, e)}
        >{buttonText}</Button>
      </Box>
      <Box component={Container} maxWidth="desktop" ref={cardsRef} sx={{position: 'relative', zIndex: 2}}>
        {pricingCards.length && (
          <>
            <Box sx={{
              mb: 8,
              mx: {
                tablet: 8,
                laptop: -1,
                desktop: -1.5
              },
              display: {
                xs: 'none',
                tablet: 'flex'
              },
              flexWrap: 'wrap'
            }}>
              {pricingCards.map(pricingCard => (
                <PricingCard
                  key={pricingCard.title}
                  {...pricingCard}
                  sx={{
                    minWidth: {
                      tablet: 270,
                      laptop: 'initial'
                    },
                    mb: {
                      tablet: 3,
                      laptop: 0
                    }
                  }}
                />
              ))}
            </Box>
            <MobilePricingTabs pricingCards={pricingCards} />
          </>
        )}
        {callout?.useCallout && (
          <Callout
            {...callout}
            sx={{
              display: {
                xs: 'none',
                tablet: 'flex'
              },
              mt: 4,
              mb: {
                xs: 8,
                tablet: 12
              }
            }}
            onClick={e => rudderslabTrackOnClick('callout', callout.title, e)}
          />
        )}
      </Box>
    </Box>
  )
}